export type VisitorInformationFieldConfig = {
  enabled: boolean;
  required: boolean;
};

export type VisitorInformationConfig = {
  enabled: boolean;
  fields: {
    name: VisitorInformationFieldConfig;
    email: VisitorInformationFieldConfig;
    phone: VisitorInformationFieldConfig;
  };
};

export type VisitorInformationValues = {
  name: string;
  email: string;
  phone: string;
};

export type VisitorInformationErrors = Partial<
  Record<keyof VisitorInformationValues, string>
>;

export const DEFAULT_VISITOR_INFORMATION: VisitorInformationConfig = {
  enabled: false,
  fields: {
    name: { enabled: true, required: true },
    email: { enabled: true, required: false },
    phone: { enabled: false, required: false },
  },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[\d\s().-]{7,32}$/;

export function resolveVisitorInformationConfig(
  value: VisitorInformationConfig | null | undefined,
): VisitorInformationConfig {
  if (!value || typeof value !== 'object') {
    return {
      enabled: false,
      fields: {
        name: { ...DEFAULT_VISITOR_INFORMATION.fields.name },
        email: { ...DEFAULT_VISITOR_INFORMATION.fields.email },
        phone: { ...DEFAULT_VISITOR_INFORMATION.fields.phone },
      },
    };
  }

  return {
    enabled: Boolean(value.enabled),
    fields: {
      name: {
        enabled: Boolean(value.fields?.name?.enabled),
        required:
          Boolean(value.fields?.name?.enabled) &&
          Boolean(value.fields?.name?.required),
      },
      email: {
        enabled: Boolean(value.fields?.email?.enabled),
        required:
          Boolean(value.fields?.email?.enabled) &&
          Boolean(value.fields?.email?.required),
      },
      phone: {
        enabled: Boolean(value.fields?.phone?.enabled),
        required:
          Boolean(value.fields?.phone?.enabled) &&
          Boolean(value.fields?.phone?.required),
      },
    },
  };
}

export function validateVisitorInformationForm(
  config: VisitorInformationConfig,
  values: VisitorInformationValues,
): VisitorInformationErrors {
  const errors: VisitorInformationErrors = {};
  const name = values.name.trim();
  const email = values.email.trim();
  const phone = values.phone.trim();

  if (config.fields.name.enabled) {
    if (config.fields.name.required && !name) {
      errors.name = 'Please enter your name.';
    } else if (name.length > 255) {
      errors.name = 'Name is too long.';
    }
  }

  if (config.fields.email.enabled) {
    if (config.fields.email.required && !email) {
      errors.email = 'Please enter your email.';
    } else if (email && !EMAIL_RE.test(email)) {
      errors.email = 'Please enter a valid email address.';
    } else if (email.length > 255) {
      errors.email = 'Email is too long.';
    }
  }

  if (config.fields.phone.enabled) {
    if (config.fields.phone.required && !phone) {
      errors.phone = 'Please enter your phone number.';
    } else if (phone && !PHONE_RE.test(phone)) {
      errors.phone = 'Please enter a valid phone number.';
    } else if (phone.length > 64) {
      errors.phone = 'Phone number is too long.';
    }
  }

  return errors;
}

export function isVisitorInformationFormValid(
  config: VisitorInformationConfig,
  values: VisitorInformationValues,
): boolean {
  return (
    Object.keys(validateVisitorInformationForm(config, values)).length === 0
  );
}

export function toInitProfile(
  config: VisitorInformationConfig,
  values: VisitorInformationValues,
): {
  displayName?: string;
  email?: string;
  phoneNumber?: string;
} {
  const profile: {
    displayName?: string;
    email?: string;
    phoneNumber?: string;
  } = {};

  if (config.fields.name.enabled) {
    const name = values.name.trim();
    if (name) profile.displayName = name.slice(0, 255);
  }
  if (config.fields.email.enabled) {
    const email = values.email.trim().toLowerCase();
    if (email) profile.email = email.slice(0, 255);
  }
  if (config.fields.phone.enabled) {
    const phone = values.phone.trim();
    if (phone) profile.phoneNumber = phone.slice(0, 64);
  }

  return profile;
}
